import { Button, NumberInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconClock } from '@tabler/icons-react';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { api } from '~/trpc-extra/api';
import { selectedOpToOpModel } from '~/server/logic/steps/operation/op-model-to-seleceted-op-converter';
import { ICON_SIZE } from './OperationControl';
import {
  DEFAULT_ACTION_TIMEOUT_MS,
  DEFAULT_ASSERTION_TIMEOUT_MS,
  DEFAULT_NAVIGATION_TIMEOUT_MS,
} from '~/common/action-timeout/constants';
import { match } from 'ts-pattern';
import { useProjectId } from '~/hooks/context/useProjectId';
import { overrideTimeouts, clearTimeoutOverride } from '~/server/logic/steps/operation/override-timeout';
import { useEffect } from 'react';
import { MIN_TIMEOUT_MS, MAX_TIMEOUT_MS } from '~/common/action-timeout/constants';

const TIMEOUT_FORM_SCHEMA = z.object({
  // These timeout limits are arbitrary and can be changed if needed
  timeoutMs: z
    .number()
    .min(MIN_TIMEOUT_MS, `Timeout must be at least ${MIN_TIMEOUT_MS}ms`)
    .max(MAX_TIMEOUT_MS, `Timeout cannot exceed ${MAX_TIMEOUT_MS}ms`),
});

export function TimeoutOverrideFormModal({
  operation,
  testContentId,
}: {
  operation: SelectedOperation;
  testContentId: string;
}) {
  const trpcUtils = api.useUtils();
  const { mutateAsync: upsertOperation, isPending } = api.operation.upsertOperation.useMutation({
    onSuccess: async () => {
      await Promise.allSettled([
        trpcUtils.testContent.get.invalidate({ id: testContentId }),
        trpcUtils.testContent.getCode.invalidate({ testContentId }),
      ]);
      modals.closeAll();
    },
  });

  const projectId = useProjectId();
  const { data: project, isLoading: projectIsLoading } = api.project.get.useQuery(
    { projectId: projectId || '' },
    { enabled: !!projectId },
  );

  const currentTimeoutMs =
    operation.actionOp?.options?.timeout ??
    operation.assertionOp?.options?.timeout ??
    operation.navigationOp?.options?.timeout;

  const defaultTimeoutMs = match(operation.type)
    .with('ACTION', () => project?.actionTimeoutMs ?? DEFAULT_ACTION_TIMEOUT_MS)
    .with('ASSERTION', () => project?.assertionTimeoutMs ?? DEFAULT_ASSERTION_TIMEOUT_MS)
    .with('NAVIGATION', () => project?.navigationTimeoutMs ?? DEFAULT_NAVIGATION_TIMEOUT_MS)
    .otherwise(() => DEFAULT_ACTION_TIMEOUT_MS);

  const operationTypeLabel = match(operation.type)
    .with('ACTION', () => 'Action')
    .with('ASSERTION', () => 'Assertion')
    .with('NAVIGATION', () => 'Navigation')
    .otherwise(() => 'Action');

  const form = useForm({
    mode: 'controlled',
    validate: zod4Resolver(TIMEOUT_FORM_SCHEMA),
    initialValues: {
      timeoutMs: currentTimeoutMs ?? defaultTimeoutMs,
    },
  });

  // Update form value when project data loads (to use project-level timeout settings)
  useEffect(() => {
    if (currentTimeoutMs === undefined && !projectIsLoading) {
      form.setFieldValue('timeoutMs', defaultTimeoutMs);
    }
  }, [projectIsLoading]);

  return (
    <form
      onSubmit={form.onSubmit(async ({ timeoutMs }) => {
        const operationModel = selectedOpToOpModel(operation);
        await upsertOperation({ operation: overrideTimeouts(operationModel, timeoutMs) });
      })}
      className="flex flex-col gap-2"
    >
      <NumberInput
        allowNegative={false}
        disabled={projectIsLoading}
        className="mb-2"
        placeholder="Enter timeout in milliseconds"
        label={
          <div className="flex items-center gap-2">
            <IconClock size={ICON_SIZE} />
            {operationTypeLabel} Timeout (milliseconds)
          </div>
        }
        description={`Override the default timeout for this ${operationTypeLabel.toLowerCase()}. Project default is ${defaultTimeoutMs}ms. Value must be between 1000 and 300000 milliseconds.`}
        min={MIN_TIMEOUT_MS}
        max={MAX_TIMEOUT_MS}
        {...form.getInputProps('timeoutMs')}
      />
      <div className={`flex gap-1 ${currentTimeoutMs !== undefined ? 'justify-between' : 'justify-end'}`}>
        {currentTimeoutMs !== undefined && (
          <Button
            onClick={async () => {
              const operationModel = selectedOpToOpModel(operation);
              await upsertOperation({ operation: clearTimeoutOverride(operationModel) });
            }}
            className="mt-2 text-red-500"
            variant="default"
            loading={isPending}
          >
            Reset
          </Button>
        )}
        <div className="flex gap-1">
          <Button
            onClick={modals.closeAll}
            className="mt-2"
            variant="default"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="mt-2"
            loading={isPending}
          >
            Save Timeout
          </Button>
        </div>
      </div>
    </form>
  );
}
